import { useState } from 'react';
import { Badge, Button, Group, Select, Stack, Table, Text, Textarea } from '@mantine/core';
import { IconPlayerPlay } from '@tabler/icons';
import { extractPlaygroundError } from '../../../api/integrations-playground-service';
import { usePlaygroundInvoke } from '../../../queries/use-integrations-playground';
import type { PlaygroundClassifiedError, PlaygroundPageDiagnostic } from '../../../api/types';
import { PLAYGROUND_METHODS, getMethodSpec } from './playground-method-catalog';
import { PlaygroundErrorDisplay } from './playground-error-display';
import { JsonBlock } from './json-block';

interface PlaygroundInvokePanelProps {
  accountId: string;
  integrationKey: string;
}

const METHOD_OPTIONS = PLAYGROUND_METHODS.map((spec) => ({
  value: spec.value,
  label: spec.value,
  group: spec.group,
}));

function templateFor(method: string): string {
  const spec = getMethodSpec(method);
  return JSON.stringify(spec?.argsTemplate ?? {}, null, 2);
}

function PagesTable({ pages }: { pages: PlaygroundPageDiagnostic[] }) {
  return (
    <Table striped withBorder fontSize="xs">
      <thead>
        <tr>
          <th>Page</th>
          <th>Items</th>
          <th>Has more</th>
          <th>API calls</th>
          <th>Duration (ms)</th>
        </tr>
      </thead>
      <tbody>
        {pages.map((page) => (
          <tr key={page.pageIndex}>
            <td>{page.pageIndex}</td>
            <td>{page.itemCount}</td>
            <td>{page.pageInfo?.hasMore ? 'yes' : 'no'}</td>
            <td>{page.pageInfo?.apiCalls ?? '—'}</td>
            <td>{page.durationMs}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export function PlaygroundInvokePanel({ accountId, integrationKey }: PlaygroundInvokePanelProps) {
  const [method, setMethod] = useState<string>('validateCredentials');
  const [argsText, setArgsText] = useState<string>(templateFor('validateCredentials'));
  const [localError, setLocalError] = useState<string | null>(null);

  const mutation = usePlaygroundInvoke();
  const spec = getMethodSpec(method);

  const handleMethodChange = (value: string | null) => {
    if (!value) return;
    setMethod(value);
    setArgsText(templateFor(value));
    setLocalError(null);
  };

  const handleRun = () => {
    let args: Record<string, unknown> = {};
    if (argsText.trim()) {
      try {
        const parsed = JSON.parse(argsText);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          setLocalError('args must be a JSON object.');
          return;
        }
        args = parsed;
      } catch {
        setLocalError('args must be valid JSON.');
        return;
      }
    }
    setLocalError(null);
    mutation.mutate({ accountId, integrationKey, method, args });
  };

  const envelope = mutation.data;

  return (
    <Stack spacing="md">
      <Select
        label="SDK method"
        data={METHOD_OPTIONS}
        value={method}
        onChange={handleMethodChange}
        searchable
        maxDropdownHeight={360}
      />
      {spec?.note && (
        <Text size="xs" color="dimmed">
          {spec.note}
        </Text>
      )}

      <Textarea
        label="Args (JSON)"
        autosize
        minRows={4}
        value={argsText}
        onChange={(event) => setArgsText(event.currentTarget.value)}
        styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
      />

      {localError && (
        <Text size="sm" color="red">
          {localError}
        </Text>
      )}

      <Group>
        <Button
          leftIcon={<IconPlayerPlay size={16} />}
          color="bagel"
          onClick={handleRun}
          loading={mutation.isPending}
        >
          Invoke
        </Button>
      </Group>

      {mutation.isError && (
        <PlaygroundErrorDisplay error={extractPlaygroundError(mutation.error)} />
      )}

      {envelope && (
        <Stack spacing="sm">
          <Group spacing="xs">
            <Badge color="bagel" variant="light">
              {envelope.method}
            </Badge>
            <Badge color="gray" variant="light">
              {envelope.durationMs} ms
            </Badge>
            {envelope.truncated && (
              <Badge color="yellow" variant="light">
                truncated
              </Badge>
            )}
            {envelope.timedOut && (
              <Badge color="orange" variant="light">
                timed out
              </Badge>
            )}
          </Group>

          {envelope.error && <PlaygroundErrorDisplay error={toDisplayError(envelope.error)} />}

          {envelope.pages && envelope.pages.length > 0 && (
            <Stack spacing={4}>
              <Text size="xs" color="dimmed" tt="uppercase" sx={{ letterSpacing: 0.4 }}>
                Page diagnostics
              </Text>
              <PagesTable pages={envelope.pages} />
            </Stack>
          )}

          {envelope.result !== undefined && <JsonBlock value={envelope.result} label="Result" />}
        </Stack>
      )}
    </Stack>
  );
}

function toDisplayError(error: PlaygroundClassifiedError) {
  return { errorType: error.errorType, message: error.originError };
}
